import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useLinkCashaca } from '~/api/user';
import { Button, Dialog, ExternalLink } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import { formStyle } from './ExternalSystemModals.css';

type Props = {
  onClose: () => void;
};

export const CashacaModal = ({ onClose }: Props) => {
  const mutation = useLinkCashaca();

  const { control, handleSubmit } = useForm<{ apiToken: string }>({
    defaultValues: { apiToken: '' },
  });

  const { t } = useTranslation(['common', 'settings', 'validation']);

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data.apiToken, {
      onSuccess: () => {
        toast.success({ description: t('settings:alerts.integrations.successCashacaPaired') });
        onClose();
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  });

  return (
    <Dialog.Root onOpenChange={(open) => !open && onClose()} open>
      <Dialog.Body>
        <Dialog.Header
          title={t('settings:integrations.externalSystems.pairWith', {
            system: t('settings:integrations.externalSystems.systems.cashaca.name'),
          })}
        />

        <p>
          <Trans components={{ a: <ExternalLink /> }} i18nKey="settings:integrations.externalSystems.systems.cashaca.modal.description" />
        </p>

        <Form className={formStyle} onSubmit={onSubmit}>
          <Controller
            control={control}
            name="apiToken"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
              <Field.Root invalid={invalid} name={name}>
                <Field.Label>{t('settings:integrations.externalSystems.systems.cashaca.modal.apiToken')}</Field.Label>

                <Input
                  onBlur={onBlur}
                  onValueChange={onChange}
                  placeholder={t('settings:integrations.externalSystems.systems.cashaca.modal.apiToken')}
                  ref={ref}
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
            }}
          />

          <Dialog.Actions>
            <Button loading={mutation.isPending} type="submit">
              {t('settings:integrations.pair')}
            </Button>
          </Dialog.Actions>
        </Form>
      </Dialog.Body>
    </Dialog.Root>
  );
};
